/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useState} from 'react';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getIOProfiles} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {IOProfile, Link} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';

/**
 * Defines the prop required by this component.
 */
export type IOProfileChooserProps = {
  defaultIOProfile: Link;
  accountId: number;
  includeNone?: boolean
  validationError: string;
  onChange: (profile: IOProfile) => void;
}

/**
 * @IOProfileChooser
 *
 * The IOProfileChooser provides a list of IOProfiles for display within a dropdown list
 * allowing the user to select a IOProfile from the list.
 *
 * ```typescript
 *
 *     import IOProfileChooser from '@ion/dls/src/editors/';
 *
 *     <IOProfileChooser defaultIOProfile={ioprofile}
 *        accountId={accountId}
 *        validationError=""
 *        onChange={selectedIOProfileCallback} />
 *
 * ```
 *
 * @param defaultIOProfile A default IOProfile to show if none selected.
 * @param accountId AccountId number from group against which IOProfile's are to be retrieved.
 * @param validationError "true" displays an error state in the component.
 * @param onChange Callback prop which will be invoked with the selected IOProfile.
 * @param includeNone When true (default), localized "None" option will be displayed.
 */
export const IOProfileChooser = ({
  defaultIOProfile,
  accountId,
  validationError,
  onChange,
  includeNone = true
}: IOProfileChooserProps) => {
  const [isLoading, setIsLoading]   = useState<boolean>(false);
  const [options, setOptions]       = useState<Options[]>([]);
  const [selected, setSelected]     = useState<string>('');
  const [ioProfiles, setIOProfiles] = useState<IOProfile[]>([]);
  const hasMounted                  = useHasMounted();

  // whenever the account changes the IOProfiles are reloaded ...
  useEffect(() => {
    accountId && getProfiles(accountId);
  }, [accountId]);

  // if a default IOProfile is provided set it as the selected ...
  useEffect(() => {
    if (defaultIOProfile) {
      setDefaultProfile(defaultIOProfile);
      setSelected(defaultIOProfile.title);
    }
  }, [options]);

  // finds a IOProfile based on profileName
  const getIOProfile = (name: string, profiles: IOProfile[]): IOProfile | undefined => {
    return profiles.find(({profileName}) => profileName === name);
  };

  // manages loading IOProfiles when an accountId is provided ...
  const getProfiles = async(accountId: number) => {
    setIsLoading(true);
    if (hasMounted !== false) {
      try {
        const profiles = await getIOProfiles(accountId);
        if (profiles?.length) {
          setIOProfiles(profiles);
          setOptions(createOptions({
            items     : profiles,
            valProp   : 'id',
            labelProp : 'profileName'
          }));
        }
      } catch (err) {
        error(`IOProfileChooser: Error occurred retrieving IOProfiles for account ${accountId}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // sets profileName from default IOProfile as the selected IOProfile ...
  const setDefaultProfile = (defaultProfile: IOProfile) => {
    const result: any = options.find(({value}: any) => value === entityId(defaultProfile));
    result ? setSelected(result.label) : setSelected('');
  };

  return (
    <>
      <Dropdown displaySelectedLabel
        includeNone={includeNone}
        hasError={validationError === 'true'}
        value={!isLoading ? selected : translate('common:states.loading')}
        loading={isLoading}
        options={options}
        fieldLabel={translate('common:editors.ioProfileChooser.label')}
        placeholder={translate('common:editors.ioProfileChooser.placeholder')}
        onChange={onChange ? function({target}) {
          const val = target.value as string;
          setSelected(val);
          onChange(getIOProfile(val, ioProfiles));
        } : null} />
    </>
  );
};

export default IOProfileChooser;
